/**
 * Tiny event emitter connecting the service-worker lifecycle
 * (registerServiceWorker) with the UI layer (PwaUpdatePrompt).
 *
 * Kept deliberately small: one event, no dependencies, safe to import
 * from non-React code.
 */

export interface PwaUpdateEvent {
  /** The waiting service worker that will take over after SKIP_WAITING. */
  worker: ServiceWorker
  registration: ServiceWorkerRegistration
}

type PwaEventMap = {
  updateAvailable: PwaUpdateEvent
}

type Listener<T> = (payload: T) => void

const listeners: { [K in keyof PwaEventMap]: Set<Listener<PwaEventMap[K]>> } = {
  updateAvailable: new Set(),
}

export function onPwaEvent<K extends keyof PwaEventMap>(
  event: K,
  listener: Listener<PwaEventMap[K]>,
): () => void {
  listeners[event].add(listener)
  return () => {
    listeners[event].delete(listener)
  }
}

export function emitPwaEvent<K extends keyof PwaEventMap>(
  event: K,
  payload: PwaEventMap[K],
): void {
  listeners[event].forEach((listener) => listener(payload))
}
